import { useEffect, useMemo } from 'react';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { IconButton } from 'ui-components/components/button/icon-button';
import { Divider } from 'ui-components/components/divider/divider';
import { Text, TextType, TextTypePropType } from 'ui-components/components/text';

import style from './actionable-rate-card.module.less';
import { messages } from './messages';
import RateDetails from './rate-details/rate-details';
import ServiceDetails from './service-details/service-details';
import { useProviderDetails } from './use-provider-details';
import { useServiceDetails } from './use-service-details';
import { useContainsDemoOrder } from '../../../../demos/hooks/use-contains-demo-order';
import { UpgradePlanPromotionalBanner } from '../../../../components/upgrade-plan-promotional-banner/upgrade-plan-promotional-banner';
import { FulfillmentPlanRateRecommendations } from '../../../../fulfillment-planner/rate-recommendation/recommendation/fulfillment-plan-rate-recommendations';
import { HAS_DISMISSED_RATE_CARD_UPGRADE_PROMO_BANNER } from '../../../../hooks/use-local-storage';
import { MessageDescriptorPropType } from '../../../../prop-types/intl';
import { OrderDetailsSectionId, useOrderDetailsExpanderSection } from '../../fulfillment-plan/order-details-expanded-section-provider';
import { RateCardSection } from '../section/rate-card-section';
import { RateCardWarningsAndErrors } from './warnings-and-errors/rate-card-warnings-and-errors';
import { useFulfillmentPlannerContext } from '../../../../fulfillment-planner/context';
import * as Actions from '../../../../store/csw/csw.actions';
import { AmazonShippingDisclaimer } from '../service-details-fields/amazon-shipping-disclaimer';

export const ActionableRateCard = ({
  ActionButton,
  hideCostReview = false,
  isDisabled = false,
  disabledMessage,
  carrierLogoClassName = '',
  rateAmountTextType = TextType.H2,
  refreshButtonClassName = '',
  refreshIconClassName = '',
}) => {
  const dispatch = useDispatch();
  const { isExpanded, toggleIsExpanded } = useOrderDetailsExpanderSection(OrderDetailsSectionId.RateCard);

  const {
    deliveryTime: estimatedDelivery,
    estimatedPickup,
    externalShipFromId,
    fulfillmentPlanId,
    fulfillmentType,
    serviceCollectionGuid,
    serviceIdAndSellerLabelProviderId,
    cswTypeId,
    totalCost,
    fulfillmentServiceId,
  } = useFulfillmentPlannerContext();

  const { isPickupServiceSelected, isThirdPartyBilling, hasProvidedRate, shouldShowRateShopperSelectedService, isMissingService } =
    useServiceDetails(fulfillmentPlanId, serviceIdAndSellerLabelProviderId, serviceCollectionGuid, cswTypeId, fulfillmentServiceId);

  const { labelProviderId, fulfillmentProviderId } = useProviderDetails(
    fulfillmentPlanId,
    serviceIdAndSellerLabelProviderId,
    externalShipFromId,
    fulfillmentType,
  );

  const isRateCardDisabled = isDisabled || isMissingService;
  const shouldShowServiceDetails = !isRateCardDisabled || shouldShowRateShopperSelectedService;
  const rateCardDisabledMessage = useMemo(() => {
    if (isDisabled) {
      return disabledMessage;
    }

    if (isMissingService) {
      return messages.rateCardMissingServiceSelection;
    }
  }, [isDisabled, isMissingService, disabledMessage]);

  useEffect(() => {
    dispatch(Actions.cswCompareRates(fulfillmentPlanId));
  }, [fulfillmentPlanId, dispatch]);
  const isDemoOrder = useContainsDemoOrder([fulfillmentPlanId]);

  return (
    <RateCardSection className={style['rate-card']}>
      <FulfillmentPlanRateRecommendations fulfillmentPlanId={fulfillmentPlanId} theme='dark' />
      <RateCardWarningsAndErrors isDisabled={isRateCardDisabled} />
      <div className={style['primary-content']}>
        <RateDetails
          carrierLogoClassName={carrierLogoClassName}
          disabledMessage={rateCardDisabledMessage}
          fillProviderId={fulfillmentProviderId}
          fulfillmentPlanId={fulfillmentPlanId}
          hasProvidedRate={hasProvidedRate}
          hideCostReview={hideCostReview}
          isDisabled={isRateCardDisabled}
          isThirdPartyBilling={isThirdPartyBilling}
          labelProviderId={labelProviderId}
          rateAmountTextType={rateAmountTextType}
          refreshButtonClassName={refreshButtonClassName}
          refreshIconClassName={refreshIconClassName}
          totalCost={totalCost}
        />
        <div className={style.buttons}>
          <ActionButton isDisabled={isRateCardDisabled} />
          {shouldShowServiceDetails ? (
            <div className={style['expander-container']}>
              <IconButton
                ariaLabelMessageDescriptor={messages.expanderButtonAriaLabel}
                className={style['expander-button']}
                icon={isExpanded ? faChevronUp : faChevronDown}
                isDisabled={isRateCardDisabled}
                onClick={toggleIsExpanded}
              />
            </div>
          ) : null}
        </div>

        {isDemoOrder ? (
          <BannerMessageV2
            bannerContainerClassName={style['no-charge-banner-container']}
            className={style['no-charge-banner']}
            content={<Text className={style['no-charge-text']} textType={TextType.Caption} value={messages.noCharge} />}
            status={BannerStatus.Info}
          />
        ) : null}
      </div>

      {shouldShowServiceDetails && isExpanded ? (
        <>
          <Divider />
          <ServiceDetails
            estimatedDelivery={estimatedDelivery}
            estimatedPickup={estimatedPickup}
            fulfillmentPlanId={fulfillmentPlanId}
            isPickupServiceSelected={isPickupServiceSelected}
            shouldShowRateShopperSelectedService={shouldShowRateShopperSelectedService}
          />
          <AmazonShippingDisclaimer
            labelProviderId={labelProviderId}
            sellerLabelProviderId={serviceIdAndSellerLabelProviderId?.sellerLabelProviderId}
          />
        </>
      ) : null}

      <UpgradePlanPromotionalBanner
        bannerId={HAS_DISMISSED_RATE_CARD_UPGRADE_PROMO_BANNER}
        promotionalSubheaderMessage={messages.upgradePromotionalBannerTitle}
        showCloseButton
      />
    </RateCardSection>
  );
};

ActionableRateCard.propTypes = {
  ActionButton: PropTypes.elementType.isRequired,
  hideCostReview: PropTypes.bool,
  isDisabled: PropTypes.bool,
  disabledMessage: MessageDescriptorPropType,
  carrierLogoClassName: PropTypes.string,
  rateAmountTextType: TextTypePropType,
  refreshButtonClassName: PropTypes.string,
  refreshIconClassName: PropTypes.string,
};
